import { useApolloClient } from '@apollo/react-hooks';
import XLSX from 'xlsx';
import _ from 'lodash';

import ExportCourseSectionReportData from 'Domains/ExportCourseSectionReportData';
import { SYLLABUS_MODE } from 'Domains/syllabus';

import { COURSE_GRADE_STAT_TYPE, COURSE_GRADE_TYPE } from '../CourseGradeReportData';
import { getCourseGradeReport, getTKCourseGradeReport } from '../../GraphQL/queries/report.query';
import { formatSchoolTerm, alignClassCoursesByCode } from '../utils';

const SHEET_NAME = {
  attendanceRate: 'ภาพรวมการเข้าเรียน',
  examinationRate: 'ภาพรวมการเข้าสอบ',
  examinationAverageResult: 'ภาพรวมคะแนน',
  growthRate: '% พัฒนาการ',
  mastery: 'ระดับความเชี่ยวชาญ',
};

export const convertCourseGradeReportToAOA = (courseGradeReport, sheetType, mode) => {
  const {
    schoolGrade: { shortName: schoolGrade },
    classes: rawClasses = [],
    courses = [],
  } = courseGradeReport;
  // Match each class's courses to the header columns by course code, not position.
  const classes = alignClassCoursesByCode(rawClasses, courses);
  return classes.map(klass =>
    mode === SYLLABUS_MODE.TRADITIONAL
      ? [
          `${schoolGrade}/${klass.classNo}`,
          ...klass.courses.map(course => {
            if (!course || course[sheetType] == null) return null;
            if (sheetType === COURSE_GRADE_TYPE.GROWTH_RATE) {
              const formatValue = `${_.round(course[sheetType], 2)}%`;
              return course[sheetType] > 0 ? `+${formatValue}` : formatValue;
            }
            return course[sheetType];
          }),
        ]
      : [
          `${schoolGrade}/${klass.classNo}`,
          klass.totalStudents,
          ...klass.courses.flatMap(course => {
            // Keep the 4 cells per course so later columns stay under the right header.
            if (!course || course[sheetType] === null) return Array(4).fill(null);
            if (sheetType === COURSE_GRADE_TYPE.GROWTH_RATE) {
              return Object.entries(course[sheetType]).map(([key, value]) => {
                const formatted = `${_.round(value, 2)}%`;
                return value > 0 ? `+${formatted}` : formatted;
              });
            } else if (sheetType === COURSE_GRADE_TYPE.MASTERY) {
              return [course.masteryLV1, course.masteryLV2, course.masteryLV3, course.masteryLV4];
            } else {
              return Object.values(course[sheetType]).map(value => {
                return value;
              });
            }
          }),
        ]
  );
};

const formatReportToAOAHeader = (courseGradeReport, mode, sheetType) => {
  const {
    subject: { name: subject },
    courses = [],
  } = courseGradeReport;

  const courseNames = courses.map(course => course.courseName);
  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    return [
      ['', ...Array(courses.length).fill(subject)],
      ['ห้อง', ...courseNames],
    ];
  }

  if (sheetType === COURSE_GRADE_TYPE.MASTERY) {
    const duplicateCourseNameForAOAHeader = courseNames.flatMap(courseName =>
      Array(4).fill(courseName)
    );
    const masteryLevelName = ['เริ่มตั้งไข่', 'ตั้งไข่', 'เข้าใจ', 'เชี่ยวชาญ'];
    const duplicateMasteryLevelNameForAOAHeader = courseNames.flatMap(() => masteryLevelName);

    return [
      ['', '', ...Array(duplicateCourseNameForAOAHeader.length).fill(subject)],
      ['', '', ...duplicateCourseNameForAOAHeader],
      ['ห้อง', 'จำนวนนักเรียน', ...duplicateMasteryLevelNameForAOAHeader],
    ];
  } else {
    const duplicateCourseNameForAOAHeader = courseNames.flatMap(courseName =>
      Array(4).fill(courseName)
    );
    const statTypeName = ['ค่าต่ำสุด', 'ค่าสูงสุด', 'ค่าเฉลี่ย', 'มัธยฐาน'];
    const duplicateStatTypeNameForAOAHeader = courseNames.flatMap(() => statTypeName);

    return [
      ['', '', ...Array(duplicateCourseNameForAOAHeader.length).fill(subject)],
      ['', '', ...duplicateCourseNameForAOAHeader],
      ['ห้อง', 'จำนวนนักเรียน', ...duplicateStatTypeNameForAOAHeader],
    ];
  }
};

const createAndFormatWorksheet = (formattedReportAOA, mode, sheetType, courseGradeReport) => {
  const worksheet = XLSX.utils.aoa_to_sheet(formattedReportAOA);
  const cols =
    mode === SYLLABUS_MODE.TRADITIONAL
      ? [
          { wch: 10 }, // ห้อง
          ...Array(formattedReportAOA[0].length - 1).fill({ wch: 15 }), // หน่วยการเรียนรู้
        ]
      : [
          { wch: 10 }, // ห้อง
          { wch: 10 }, // จำนวนนักเรียน
          ...Array(formattedReportAOA[0].length - 2).fill({ wch: 15 }), // หน่วยการเรียนรู้
        ];

  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    worksheet['!merges'] = [
      { s: { r: 0, c: 1 }, e: { r: 0, c: cols.length - 1 } }, // ชื่อวิชา
    ];
  } else {
    worksheet['!merges'] = [
      { s: { r: 0, c: 2 }, e: { r: 0, c: cols.length - 1 } }, // ชื่อวิชา
    ];

    const { courses = [] } = courseGradeReport;
    const courseNames = courses.map(course => course.courseName);

    let courseStart = 2;
    for (const course of courseNames) {
      worksheet['!merges'].push({ s: { r: 1, c: courseStart }, e: { r: 1, c: courseStart + 3 } });
      courseStart += 4;
    }
  }

  worksheet['!cols'] = cols;
  return worksheet;
};

const ExportCourseGradeReportData = props => {
  const { schoolTermId, schoolGradeId, subjectId, schoolCode, children, mode } = props;

  const SHEETS =
    mode === SYLLABUS_MODE.TRADITIONAL
      ? [
          COURSE_GRADE_TYPE.EXAMINATION_AVERAGE_RESULT,
          COURSE_GRADE_TYPE.ATTENDANCE_RATE,
          COURSE_GRADE_TYPE.EXAMINATION_RATE,
          COURSE_GRADE_TYPE.GROWTH_RATE,
        ]
      : [
          COURSE_GRADE_TYPE.ATTENDANCE_RATE,
          COURSE_GRADE_TYPE.EXAMINATION_RATE,
          COURSE_GRADE_TYPE.MASTERY,
          COURSE_GRADE_TYPE.GROWTH_RATE,
        ];

  const client = useApolloClient();

  const fetchCourseGradeReportData = async () => {
    try {
      const { data } = await client.query({
        query: mode === SYLLABUS_MODE.TRADITIONAL ? getCourseGradeReport : getTKCourseGradeReport,
        variables: {
          schoolTermId,
          schoolGradeId,
          subjectId,
          schoolCode,
        },
      });
      return { data };
    } catch (error) {
      return { error };
    }
  };

  const exportCourseGradeReport = async ({ report: courseGradeReport, type }) => {
    const {
      schoolGrade: { shortName: schoolGrade } = {},
      schoolTerm: { shortName: schoolTerm } = {},
      subject: { name: subject } = {},
    } = courseGradeReport;

    const workbook = XLSX.utils.book_new();
    SHEETS.forEach(sheetType => {
      const formattedReportAOAHeader = formatReportToAOAHeader(courseGradeReport, mode, sheetType);
      const formattedReportAOAData = convertCourseGradeReportToAOA(
        courseGradeReport,
        sheetType,
        mode
      );
      const worksheet = createAndFormatWorksheet(
        [...formattedReportAOAHeader, ...formattedReportAOAData],
        mode,
        sheetType,
        courseGradeReport
      );
      XLSX.utils.book_append_sheet(workbook, worksheet, SHEET_NAME[sheetType]);
    });

    XLSX.writeFile(
      workbook,
      `ภาพรวมระดับชั้น_${formatSchoolTerm(schoolTerm)}_${schoolGrade}-ทุกห้อง_${subject}.` +
        (type || 'xlsx')
    );
  };

  const { fetchCourseSectionReportData, exportCourseSectionReport } = ExportCourseSectionReportData(
    props
  );

  return children({
    fetchCourseGradeReportData,
    exportCourseGradeReport,
    fetchCourseSectionReportData,
    exportCourseSectionReport,
  });
};

export default ExportCourseGradeReportData;
